import { getEnv } from "../env";
import type { HumanInterrupt } from "../approvals/types";
import { ApiError } from "./chatApi";

const API_URL = getEnv("VITE_API_URL") || "http://localhost:8000";

type GetToken = () => Promise<string>;

/**
 * Shape of an approval as returned by the HITL endpoints.
 * `payload` carries the standard HumanInterrupt contract (see lib/approvals/types).
 */
export interface ApprovalItem {
  correlation_id: string;
  thread_id: string;
  assistant_id: string;
  action: string;
  payload: HumanInterrupt;
  status: string;
  created_at: string;
  expires_at: string | null;
  /** Id legible generado al registrar (ej. numero_asiento X3). null si no aplica. */
  result_ref?: string | null;
  /** Cuándo se resolvió (ISO). Solo en resueltas. */
  resolved_at?: string | null;
  /** Quién la resolvió (email / apikey). Solo en resueltas. */
  resolved_by?: string | null;
  /**
   * Desenlace: 'approved' (accept/edit) | 'rejected' (ignore/response) |
   * 'reprocessed' (cerrada y re-encolada como run nuevo) | null.
   */
  outcome?: "approved" | "rejected" | "reprocessed" | null;
  /** Dict resultado del state final (numero_asiento, imputacion, estado…). */
  result?: Record<string, unknown> | null;
  /**
   * thread_id de OTRA copia pendiente del mismo documento (mismo número y proveedor),
   * o null. Lo marca el backend cuando dos documentos del mismo email se indexan a la
   * vez y la comprobación del grafo no pudo verse. Ambas copias siguen pendientes: el
   * revisor decide cuál aprueba.
   */
  duplicate_of?: string | null;
}

/** Entrada del registro de actividad: un run que NO genero tarea de aprobacion. */
export interface ActivityItem {
  id: string;
  assistant_id: string;
  thread_id: string;
  status: string;
  /** Codigo tecnico del motivo; el front lo traduce (con fallback al codigo). */
  reason: string;
  error: string | null;
  created_at: string | null;
  processed_at: string | null;
  sender: string | null;
  origen: string | null;
  numero_factura: string | null;
  documento: string | null;
}

function createApprovalsClient(getToken: GetToken, onAuthFailure?: () => void) {
  async function headers(contentType = "application/json") {
    const token = await getToken();
    const h: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (contentType) h["Content-Type"] = contentType;
    return h;
  }

  // Retry once on 401 (token may have expired between check and request)
  async function fetchWithAuth(
    url: string,
    init?: RequestInit,
    contentType = "application/json",
  ): Promise<Response> {
    const res = await fetch(url, { ...init, headers: await headers(contentType) });
    if (res.status === 401) {
      const retryRes = await fetch(url, { ...init, headers: await headers(contentType) });
      if (retryRes.status === 401) {
        onAuthFailure?.();
        throw new Error("Authentication failed");
      }
      return retryRes;
    }
    return res;
  }

  async function listApprovals(params?: {
    status?: string;
    assistant_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    approvals: ApprovalItem[];
    can_approve?: boolean;
    has_more?: boolean;
    /** Agentes HITL que el usuario puede atender (grupos fijos del sidebar). */
    hitl_agents?: string[];
  }> {
    const url = new URL(`${API_URL}/approvals`);
    if (params?.status) url.searchParams.set("status", params.status);
    if (params?.assistant_id) url.searchParams.set("assistant_id", params.assistant_id);
    if (typeof params?.limit === "number") url.searchParams.set("limit", String(params.limit));
    if (typeof params?.offset === "number") url.searchParams.set("offset", String(params.offset));
    const res = await fetchWithAuth(url.toString());
    if (!res.ok) throw new ApiError(`Failed to list approvals: ${res.status}`, res.status);
    return res.json();
  }

  async function listActivity(params?: {
    assistant_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ activity: ActivityItem[]; has_more?: boolean }> {
    const url = new URL(`${API_URL}/activity`);
    if (params?.assistant_id) url.searchParams.set("assistant_id", params.assistant_id);
    if (typeof params?.limit === "number") url.searchParams.set("limit", String(params.limit));
    if (typeof params?.offset === "number") url.searchParams.set("offset", String(params.offset));
    const res = await fetchWithAuth(url.toString());
    if (!res.ok) throw new ApiError(`Failed to list activity: ${res.status}`, res.status);
    return res.json();
  }

  async function getApproval(correlationId: string): Promise<ApprovalItem> {
    const res = await fetchWithAuth(`${API_URL}/approvals/${encodeURIComponent(correlationId)}`);
    if (!res.ok) throw new ApiError(`Failed to get approval: ${res.status}`, res.status);
    return res.json();
  }

  async function resumeApproval(
    correlationId: string,
    response: { type: string; args: unknown },
  ): Promise<{ status: string; detail?: string }> {
    const res = await fetchWithAuth(
      `${API_URL}/approvals/${encodeURIComponent(correlationId)}/resume`,
      {
        method: "POST",
        body: JSON.stringify(response),
      },
    );
    if (!res.ok) throw new ApiError(`Failed to resume approval: ${res.status}`, res.status);
    return res.json();
  }

  /** Cierra la pendiente como "reprocesada" y re-encola su documento como run nuevo. */
  async function reprocessApproval(
    correlationId: string,
  ): Promise<{ status: string; reprocesada_en?: string; detail?: string }> {
    const res = await fetchWithAuth(
      `${API_URL}/approvals/${encodeURIComponent(correlationId)}/reprocess`,
      { method: "POST" },
    );
    if (!res.ok) throw new ApiError(`Failed to reprocess approval: ${res.status}`, res.status);
    return res.json();
  }

  return {
    listApprovals,
    listActivity,
    getApproval,
    resumeApproval,
    reprocessApproval,
  };
}

export { createApprovalsClient };
export type ApprovalsClient = ReturnType<typeof createApprovalsClient>;
